import { ComponentType } from 'react';
import { INPUT_TYPES } from '@components/formInputTypes';
import { DestinationType } from '../../test/mock/typeNameMap';
import { INTERNAL_ROLES, ROLES } from '../../types';
import { regionOptions } from '../Authentications/authenticationSourceConfigs';
import { IntegrationWithConfig, TARGET_MATCHING_TYPE_ENUM } from './types';

interface AuthSection {
  shouldShow: () => boolean;
  fields: unknown[];
  authType: string;
}
export interface FieldConfig {
  label: string;
  valueName: string;
  required?: boolean | ((config: any) => boolean);
  type?: keyof typeof INPUT_TYPES | ComponentType<any>;
  defaultValue?: any;
  shouldShow?: (config: any) => boolean;
  params?: {
    placeholder?: string;
    options?: { title: string; value: string }[];
    isMultiple?: boolean;
    value?: string;
  };
  disabled?: (isDisabled: boolean) => boolean;
  isGetOptionsFromContext?: boolean;
  requiredRole?: (typeof ROLES)[keyof typeof ROLES];
  requiredRoleName?: (typeof INTERNAL_ROLES)[keyof typeof INTERNAL_ROLES];
  projectionName?: string;
}

interface DestinationConfig {
  fields?: FieldConfig[];
  settingsFields?: unknown[];
  authSection?: AuthSection;
  onPremSection?: {
    shouldShow: boolean;
  };
  defaultFields?: Partial<IntegrationWithConfig>;
  uiComponentsConfig?: { disableProjectionSelect?: boolean };
}

export const AuthTypes = {
  OAUTH2: 'OAUTH2',
  OAUTH2_REFRESH_TOKEN: 'OAUTH2 Refresh Token',
  JWT: 'JWT',
};
const serviceNowTablesOptions = [
  { title: 'Incidents', value: 'incident' },
  { title: 'Requests', value: 'sc_req_item' },
  { title: 'Remediation Task', value: 'x_aai_bprm_bprm_task' },
  { title: 'Tasks', value: 'sc_task' },
];
const snowCmdbTablesOptions = [
  { title: 'CMDB CI Server', value: 'cmdb_ci_server' },
  { title: 'CMDB CI Computer', value: 'cmdb_ci_computer' },
  { title: 'CMDB CI VM', value: 'cmdb_ci_vm_instance' },
];

const snowIRMTablesOptions = [{ title: 'Policy Exceptions', value: 'sn_compliance_policy_exception' }];

const authenticationTypes = [
  { title: 'OAuth2', value: 'OAUTH2' },
  { title: 'Basic Auth', value: 'BASIC' },
];
const authenticationTypesForJira = [
  { title: 'Basic Auth', value: 'BASIC' },
  { title: 'Bearer Token', value: 'BEARER' },
];
const grantTypeOptions = [
  { title: 'Password', value: 'PASSWORD' },
  { title: 'Refresh Token', value: 'REFRESH_TOKEN' },
  { title: 'Client Credentials', value: 'CLIENT_CREDENTIALS' },
];
const bmcHelixIssueTypeOptions = [
  { title: 'Incidents', value: 'Incident' },
  { title: 'Work Orders', value: 'WorkOrder' },
];
const methodOptions = [
  { title: 'Post', value: 'POST' },
  { title: 'Put', value: 'PUT' },
  { title: 'Patch', value: 'PATCH' },
];
const anyTargetIssueLinkSuffixOptions = [
  { title: 'Issue Key', value: 'ISSUE_KEY' },
  { title: 'Display Key', value: 'DISPLAY_KEY' },
];

function GetSnowConfig(additionalFields: any[] = []) {
  return {
    fields: [
      { label: 'Instance Name', valueName: 'domain', required: true },
      ...additionalFields,
      {
        label: 'Authentication',
        valueName: 'authType',
        type: INPUT_TYPES.RADIO,
        params: {
          options: authenticationTypes,
        },
        shouldShow: () => false,
        defaultValue: 'OAUTH2',
      },
      {
        label: 'Grant Type',
        valueName: 'grantType',
        required: true,
        shouldShow: () => false,
        type: INPUT_TYPES.SELECT,
        defaultValue: 'PASSWORD',
        params: {
          options: [
            { title: 'Password', value: 'PASSWORD' },
            { title: 'Refresh Token', value: 'REFRESH_TOKEN' },
          ],
        },
      },
      {
        label: 'Username',
        valueName: 'username',
        required: true,
        shouldShow: () => false,
        defaultValue: 'auth-config',
      },
      {
        label: 'Password',
        valueName: 'password',
        required: true,
        shouldShow: () => false,
        defaultValue: 'auth-config',
      },
      {
        label: 'Client Id',
        valueName: 'clientId',
        required: true,
        shouldShow: () => false,
        defaultValue: 'auth-config',
      },
      {
        label: 'Client Secret',
        valueName: 'clientSecret',
        required: true,
        shouldShow: () => false,
        defaultValue: 'auth-config',
      },
    ],
    authSection: {
      shouldShow: () => false,
      fields: [
        { label: 'Client Id', valueName: 'oauthClientId', required: true },
        { label: 'Client Secret', valueName: 'oauthClientSecret', required: true },
        { label: 'Instance Name', valueName: 'oauthTenantId', required: true },
        { label: 'Refresh Token', valueName: 'oauthRefreshToken', required: true },
      ],
      authType: AuthTypes.OAUTH2_REFRESH_TOKEN,
    },
  };
}

const jiraFields = [
  {
    label: 'Jira Organization Domain',
    valueName: 'domain',
    required: true,
    shouldShow: () => false,
    params: { placeholder: 'my-domain.atlassian.net' },
    defaultValue: 'auth-config',
  },
  { label: 'Project Key', valueName: 'projectKey', required: true },
  { label: 'Issue Type', valueName: 'issueType' }, // TODO: remove after we open the stepper FF.
  {
    label: 'Authentication',
    required: true,
    valueName: 'authType',
    type: INPUT_TYPES.RADIO,
    params: {
      options: authenticationTypesForJira,
    },
    shouldShow: () => false,
    defaultValue: 'BASIC',
  },
  {
    label: 'Email',
    valueName: 'email',
    required: true,
    shouldShow: () => false,
    defaultValue: 'auth-config',
  },
  {
    label: 'Jira API Key',
    valueName: 'apiKey',
    required: true,
    shouldShow: () => false,
    defaultValue: 'auth-config',
  },
  {
    label: 'Password',
    valueName: 'apiKey',
    required: true,
    shouldShow: () => false,
    defaultValue: 'auth-config',
  },

  {
    label: 'Proxy DNS', // TODO: should move only to Jira Data Center
    valueName: 'proxyDNS',
    required: config => config.proxyEnabled,
    shouldShow: ({ proxyEnabled }) => proxyEnabled,
  },
];

const tenableFields = [
  {
    label: 'Scan Name',
    valueName: 'scanName',
    required: false,
    defaultValue: 'UVM Scan - Basic Network Scan',
  },
  {
    label: 'Max Number of Parallel Scans',
    valueName: 'parallelScans',
    required: false,
    type: INPUT_TYPES.SELECT,
    params: {
      options: [
        { title: '1', value: 1 },
        { title: '2', value: 2 },
        { title: '3', value: 3 },
        { title: '4', value: 4 },
        { title: '5', value: 5 },
      ],
    },
    defaultValue: 1,
  },
];

export const configFields: Record<DestinationType, DestinationConfig> = {
  JIRA: {
    fields: jiraFields,
    settingsFields: [
      {
        label: 'Issue Type',
        valueName: 'issueType',
        required: true,
        type: INPUT_TYPES.SELECT,
        disabled: isDisabled => isDisabled,
        isGetOptionsFromContext: true,
      },
    ],
  },
  JIRA_DATA_CENTER: {
    fields: [...jiraFields, { label: 'Skip SSL', valueName: 'skipSSL', required: false, type: INPUT_TYPES.BOOL, defaultValue: false }],
    settingsFields: [
      {
        label: 'Issue Type',
        valueName: 'issueType',
        required: true,
        type: INPUT_TYPES.SELECT,
        disabled: isDisabled => isDisabled,
        isGetOptionsFromContext: true,
      },
    ],
    onPremSection: { shouldShow: true },
  },
  SLACK: {
    fields: [
      {
        label: 'App Token',
        valueName: 'appToken',
        required: true,
        shouldShow: () => false,
        defaultValue: 'auth-config',
      },
      {
        label: 'Channel Type',
        valueName: 'channelType',
        type: INPUT_TYPES.RADIO,
        required: true,
        defaultValue: 'PRIVATE_CHANNEL',
        params: {
          options: [
            { title: 'Private Channel', value: 'PRIVATE_CHANNEL' },
            { title: 'Public Channel', value: 'PUBLIC_CHANNEL' },
          ],
        },
      },
      { label: 'Channel', valueName: 'channel', required: true },
    ],
  },
  FRESHSERVICE: {
    fields: [
      {
        label: 'Api Key',
        valueName: 'apiKey',
        required: true,
        shouldShow: () => false,
        defaultValue: 'auth-config',
      },
      { label: 'Domain', valueName: 'domain', required: true },
    ],
  },
  TINES: {
    fields: [
      {
        label: 'Domain',
        valueName: 'domain',
        required: true,
        shouldShow: () => false,
        defaultValue: 'auth-config',
      },
      {
        label: 'User Token',
        valueName: 'userToken',
        shouldShow: () => false,
      },
      {
        label: 'User Email',
        valueName: 'userEmail',
        shouldShow: () => false,
      },
      {
        label: 'Secret',
        valueName: 'secret',
        required: true,
        shouldShow: () => false,
        defaultValue: 'auth-config',
      },
    ],
    settingsFields: [
      { label: 'Path', valueName: 'path', required: true },
      { label: 'Secret', valueName: 'secret', required: true },
    ],
  },
  TORQ: {
    fields: [
      {
        label: 'Domain',
        valueName: 'webhookUrl',
        required: true,
        shouldShow: () => false,
        defaultValue: 'auth-config',
      },
      {
        label: 'Auth Header Name',
        valueName: 'authHeaderName',
        shouldShow: () => false,
        defaultValue: 'auth-config',
      },
      {
        label: 'Auth Header Value',
        valueName: 'authHeaderValue',
        shouldShow: () => false,
        defaultValue: 'auth-config',
      },
    ],
  },
  SERVICENOW: {
    settingsFields: [
      {
        label: 'Table',
        valueName: 'tableName',
        required: true,
        type: INPUT_TYPES.OPEN_SELECT,
        defaultValue: 'incident',
        params: {
          options: serviceNowTablesOptions,
          isMultiple: false,
        },
      },
    ],
    ...GetSnowConfig(),
  },
  VEOCI: {
    fields: [
      {
        label: 'Client Id',
        valueName: 'clientId',
        required: true,
        shouldShow: () => false,
        defaultValue: 'auth-config',
      },
      {
        label: 'Client Secret',
        valueName: 'clientSecret',
        required: true,
        shouldShow: () => false,
        defaultValue: 'auth-config',
      },
      { label: 'Form Id', valueName: 'formId', required: true },
    ],
    settingsFields: [{ label: 'Avalor Id Field Name', valueName: 'avalorIdFieldName', required: true }],
  },
  RAPID7: {
    fields: [
      {
        label: 'Api Key',
        valueName: 'apiKey',
        required: true,
        shouldShow: () => false,
        defaultValue: 'auth-config',
      },
      {
        label: 'Domain',
        valueName: 'baseDomain',
        required: true,
        shouldShow: () => false,
        defaultValue: 'auth-config',
      },
    ],
    settingsFields: [
      { label: 'Workflow Id', valueName: 'workflowId', required: true },
      { label: 'Execution Message', valueName: 'executionMessage' },
    ],
  },
  MONDAY: {
    fields: [
      {
        label: 'Token',
        valueName: 'token',
        required: true,
        shouldShow: () => false,
        defaultValue: 'auth-config',
      },
    ],
    settingsFields: [{ label: 'Board Name', valueName: 'boardName', required: true }],
  },
  SCCM: {
    fields: [
      { label: 'Host', valueName: 'host', required: true },
      { label: 'Port', valueName: 'port', required: true, defaultValue: 5985 },
      { label: 'Domain', valueName: 'domain' },
      { label: 'User', valueName: 'user', required: true },
      { label: 'Password', valueName: 'password', required: true },
    ],
    settingsFields: [
      { label: 'Site Code', valueName: 'sccmSiteCode', required: true },
      { label: 'Execution Message', valueName: 'executionMessage', defaultValue: 'Triggered patch via SCCM' },
      {
        label: 'Deployment Creation Arguments',
        valueName: 'deploymentCreationArguments',
        defaultValue:
          '-SoftwareInstallation $true -DeployWithNoPackage $true -SendWakeupPacket $true -DownloadFromMicrosoftUpdate $true -AvailableDateTime (Get-Date) -DeadlineDateTime (Get-Date).AddDays(7)',
      },
      { label: 'Assets Postfix', valueName: 'assetsPostfix' },
      {
        label: 'Debug Mode For Builtin Script',
        valueName: 'debugMode',
        type: INPUT_TYPES.BOOL,
        requiredRole: ROLES.RND_ADMIN,
        requiredRoleName: INTERNAL_ROLES.RND_ADMIN,
      },
      {
        label: 'Use Debug Script',
        valueName: 'useDebugScript',
        type: INPUT_TYPES.BOOL,
        requiredRole: ROLES.RND_ADMIN,
        requiredRoleName: INTERNAL_ROLES.RND_ADMIN,
      },
      {
        label: 'Debug Script',
        valueName: 'debugScript',
        requiredRole: ROLES.RND_ADMIN,
        requiredRoleName: INTERNAL_ROLES.RND_ADMIN,
      },
    ],
  },
  BMCHELIX: {
    fields: [
      {
        label: 'Domain',
        valueName: 'domain',
        required: true,
        shouldShow: () => false,
        defaultValue: 'auth-config',
      },
      {
        label: 'Port',
        valueName: 'port',
        required: true,
        shouldShow: () => false,
        defaultValue: 'auth-config',
      },
      {
        label: 'User',
        valueName: 'username',
        required: true,
        shouldShow: () => false,
        defaultValue: 'auth-config',
      },
      {
        label: 'Password',
        valueName: 'password',
        required: true,
        shouldShow: () => false,
        defaultValue: 'auth-config',
      },
      { label: 'Use HTTPS', valueName: 'useHttps', type: INPUT_TYPES.BOOL },
    ],
    settingsFields: [
      {
        label: 'Issue Type',
        valueName: 'issueType',
        required: true,
        type: INPUT_TYPES.SELECT,
        params: { options: bmcHelixIssueTypeOptions },
      },
    ],
  },
  EMAIL: {
    fields: [
      {
        label: 'Recipients',
        valueName: 'recipients',
        required: true,
        type: INPUT_TYPES.OPEN_SELECT,
        defaultValue: [],
        params: { isMultiple: true },
      },
      {
        label: 'Subject',
        valueName: 'subject',
        required: false,
        requiredRole: ROLES.RND_ADMIN,
        requiredRoleName: INTERNAL_ROLES.RND_ADMIN,
        defaultValue: ' ',
        shouldShow: () => false,
      },
      {
        label: 'Body',
        valueName: 'body',
        required: false,
        requiredRole: ROLES.RND_ADMIN,
        requiredRoleName: INTERNAL_ROLES.RND_ADMIN,
        defaultValue: ' ',
        shouldShow: () => false,
      },
    ],
  },
  TEAMS: {
    fields: [
      { label: 'Group ID', valueName: 'groupId', required: true },
      { label: 'Channel ID', valueName: 'channelId', required: true },
    ],
    authSection: {
      shouldShow: () => false,
      fields: [
        { label: 'Client Id', valueName: 'oauthClientId', required: true },
        { label: 'Client Secret', valueName: 'oauthClientSecret', required: true },
        { label: 'Tenant Id', valueName: 'oauthTenantId', required: true },
      ],
      authType: AuthTypes.OAUTH2,
    },
  },
  S3: {
    fields: [
      { label: 'Account', valueName: 'awsAccount', required: true },
      {
        label: 'Region',
        valueName: 'region',
        required: true,
        type: INPUT_TYPES.SELECT,
        params: { options: regionOptions },
      },
      { label: 'Bucket', valueName: 'bucket', required: true },
      { label: 'Role ARN', valueName: 'roleArn', required: true },
      { label: 'External ID', valueName: 'externalId' },
    ],
    settingsFields: [{ label: 'Path', valueName: 'path' }],
  },
  GCP_STORAGE: {
    fields: [
      { label: 'Bucket', valueName: 'bucket', required: true },
      { label: 'Project ID', valueName: 'projectId' },
      { label: 'Path', valueName: 'path' },
    ],
  },
  ANYTARGET: {
    fields: [
      {
        label: 'Test Connection Path',
        valueName: 'testConnectionPath',
        required: false,
        params: { placeholder: '/rest/api/test' },
      },
      {
        label: 'Authentication Type',
        valueName: 'authType',
        required: true,
        type: INPUT_TYPES.RADIO,
        params: { options: authenticationTypes },
        defaultValue: 'OAUTH2',
      },
      {
        label: 'Username',
        valueName: 'username',
        required: config => config.authType === 'BASIC',
        shouldShow: ({ authType }) => authType === 'BASIC',
      },
      {
        label: 'Password',
        valueName: 'password',
        required: config => config.authType === 'BASIC',
        shouldShow: ({ authType }) => authType === 'BASIC',
      },
      {
        label: 'Grant Type',
        valueName: 'grantType',
        required: true,
        shouldShow: ({ authType }) => authType === 'OAUTH2',
        type: INPUT_TYPES.SELECT,
        defaultValue: 'CLIENT_CREDENTIALS',
        params: { options: grantTypeOptions },
      },
      {
        label: 'Client Id',
        valueName: 'clientId',
        required: config => config.grantType === 'CLIENT_CREDENTIALS',
        shouldShow: ({ authType, grantType }) => authType === 'OAUTH2' && grantType === 'CLIENT_CREDENTIALS',
      },
      {
        label: 'Client Secret',
        valueName: 'clientSecret',
        required: config => config.grantType === 'CLIENT_CREDENTIALS',
        shouldShow: ({ authType, grantType }) => authType === 'OAUTH2' && grantType === 'CLIENT_CREDENTIALS',
      },
      {
        label: 'Token URL',
        valueName: 'tokenUrl',
        required: config => config.grantType === 'CLIENT_CREDENTIALS',
        shouldShow: ({ authType, grantType }) => authType === 'OAUTH2' && grantType === 'CLIENT_CREDENTIALS',
      },
      {
        label: 'Auth Headers',
        valueName: 'authHeaders',
        required: false,
        params: { placeholder: '{“FieldName1”: “FieldValue1”, "FieldName2”: “FieldValue2”}' },
      },
      { label: 'Skip SSL', valueName: 'skipSSL', required: false, type: INPUT_TYPES.BOOL, defaultValue: false },
    ],
    authSection: {
      shouldShow: () => false,
      fields: [],
      authType: AuthTypes.OAUTH2_REFRESH_TOKEN,
    },
    onPremSection: { shouldShow: true },
    settingsFields: [
      { label: 'Base URL', valueName: 'baseUrl', required: true, params: { placeholder: 'https://example.com' } },

      {
        label: 'Create Issue Path',
        valueName: 'createIssuePath',
        required: true,
        params: { placeholder: '/rest/api/issue' },
      },
      { label: 'Payload Root Field Name', valueName: 'payloadRootFieldName', required: false },
      {
        label: 'Additional Create Issue Payload Fields',
        valueName: 'additionalCreateIssuePayloadField',
        required: false,
        params: { placeholder: '{"FieldName1": "FieldValue1", "FieldName2": "FieldValue2"}' },
      },
      { label: 'Issue Key Path', valueName: 'issueKeyPath', params: { placeholder: 'issue.key' } },
      {
        label: 'Async Response',
        valueName: 'asyncResponse',
        required: true,
        type: INPUT_TYPES.BOOL,
        defaultValue: false,
      },
      {
        label: 'Sub Issue Read Path',
        valueName: 'subIssueReadPath',
        required: false,
        params: { placeholder: '/rest/api/sub-issue/{subIssueReadPath}' },
      },
      {
        label: 'Sub Issue Key Path',
        valueName: 'subIssueKeyPath',
        required: false,
      },
      {
        label: 'Identifier Ticket Field Name',
        valueName: 'identifierTicketFieldName',
        required: false,
        shouldShow: ({ asyncResponse }) => asyncResponse,
      },
      { label: 'Issue Display Key Path', valueName: 'issueDisplayKeyPath', required: false },
      {
        label: 'Issue Link URL',
        valueName: 'issueLinkUrl',
        required: false,
        params: { placeholder: 'https://example.com/issue/' },
      },
      {
        label: 'Issue Link Suffix',
        valueName: 'issueLinkSuffix',
        required: false,
        params: { options: anyTargetIssueLinkSuffixOptions, placeholder: 'Issue Key' },
        type: INPUT_TYPES.SELECT,
        defaultValue: 'ISSUE_KEY',
      },
      {
        label: 'Update Issue Path',
        valueName: 'updateIssuePath',
        required: false,
        params: { placeholder: '/rest/api/issue/{externalTicketKey}' },
      },
      {
        label: 'Update Issue Method',
        valueName: 'updateIssueMethod',
        required: config => !!config.updateIssuePath,
        params: { options: methodOptions },
        type: INPUT_TYPES.RADIO,
        defaultValue: 'PUT',
      },
      {
        label: 'Webhook Key Field Name',
        valueName: 'webhookKeyFieldName',
        required: false,
      },
      {
        label: 'Webhook Change Fields Field Name',
        valueName: 'webhookChangedFieldsFieldName',
        required: false,
      },
      {
        label: 'Webhook Deleted Field Name',
        valueName: 'webhookIsDeletedFieldName',
        required: false,
      },
      {
        label: 'Comments URL',
        valueName: 'commentsUrl',
        required: false,
        params: { placeholder: 'https://example.com/ticket/{externalTicketKey}/comments' },
      },
      {
        label: 'Comments Method',
        valueName: 'commentsMethod',
        required: config => !!config.updateIssuePath,
        params: { options: methodOptions },
        type: INPUT_TYPES.RADIO,
        defaultValue: 'PUT',
      },
      {
        label: 'Comments Custom Headers',
        valueName: 'commentsCustomHeaders',
        required: false,
        params: { placeholder: '{“FieldName1”: “FieldValue1”, "FieldName2”: “FieldValue2”}' },
      },
    ],
  },
  KAFKA: {
    fields: [
      { label: 'Bootstrap Servers', valueName: 'bootstrapServers', required: true },
      {
        label: 'Topic',
        valueName: 'topic',
        required: true,
      },
    ],
    settingsFields: [],
  },
  SNOW_CMDB: {
    defaultFields: {
      projectionName: 'violation_tickets',
      targetMatchingType: TARGET_MATCHING_TYPE_ENUM.CONDITION,
      matchingCondition: { stringCondition: { in: { values: [] } }, fieldName: 'violation_ticket.title' },
    },
    settingsFields: [
      {
        label: 'Table',
        valueName: 'tableName',
        required: true,
        type: INPUT_TYPES.OPEN_SELECT,
        defaultValue: 'cmdb_ci_server',
        params: {
          options: snowCmdbTablesOptions,
          isMultiple: false,
        },
      },
    ],
    ...GetSnowConfig(),
  },
  SERVICE_NOW_SERVICE_CATALOG: {
    settingsFields: [
      {
        label: 'Table',
        valueName: 'tableName',
        required: true,
        type: INPUT_TYPES.OPEN_SELECT,
        defaultValue: 'sc_task',
        params: {
          options: serviceNowTablesOptions,
          isMultiple: false,
        },
      },
    ],
    ...GetSnowConfig([
      {
        label: 'Item ID',
        valueName: 'itemId',
        required: true,
      },
    ]),
  },
  DSI: {
    fields: [
      {
        label: 'Data Source ID',
        valueName: 'dataSourceInstanceId',
        required: true,
      },
    ],
  },
  HALO: {
    fields: [
      {
        label: 'Domain',
        valueName: 'domain',
        required: true,
        shouldShow: () => false,
        defaultValue: 'auth-config',
      },
      {
        label: 'Client Id',
        valueName: 'clientId',
        required: true,
        shouldShow: () => false,
        defaultValue: 'auth-config',
      },
      {
        label: 'Client Secret',
        valueName: 'clientSecret',
        required: true,
        shouldShow: () => false,
        defaultValue: 'auth-config',
      },
      {
        label: 'Tenant',
        valueName: 'tenant',
        required: true,
        shouldShow: () => false,
        defaultValue: 'auth-config',
      },
      {
        label: 'Issue Type',
        valueName: 'issueType',
        required: true,
        defaultValue: 'Incident',
      },
    ],
  },
  SERVICE_NOW_IRM: {
    settingsFields: [
      {
        label: 'Table',
        valueName: 'tableName',
        required: true,
        type: INPUT_TYPES.OPEN_SELECT,
        defaultValue: 'sn_compliance_policy_exception',
        params: {
          options: snowIRMTablesOptions,
          isMultiple: false,
        },
      },
    ],
    ...GetSnowConfig(),
  },
  MANAGE_ENGINE: {
    settingsFields: [{ label: 'App Domain', valueName: 'appDomain', required: false }],
    fields: [
      {
        label: 'Domain Name',
        valueName: 'domainName',
        required: true,
      },
      {
        label: 'Auth Headers',
        valueName: 'authHeaders',
        required: false,
        params: { placeholder: '{“FieldName1”: “FieldValue1”, "FieldName2”: “FieldValue2”}' },
      },
    ],
  },
  AZURE_DEVOPS: {
    settingsFields: [
      {
        label: 'Work item type',
        valueName: 'workItemType',
        required: true,
        type: INPUT_TYPES.SELECT,
        disabled: isDisabled => isDisabled,
        isGetOptionsFromContext: true,
      },
    ],
    fields: [
      { label: 'Organization', valueName: 'organization', required: true },
      { label: 'Project', valueName: 'project', required: true },
    ],
  },
  QUALYS_PM: {
    fields: [],
  },
  TENABLE: {
    defaultFields: {
      projectionName: 'uber_assets',
      targetMatchingType: TARGET_MATCHING_TYPE_ENUM.CONDITION,
    },
    uiComponentsConfig: {
      disableProjectionSelect: true,
    },
    settingsFields: [
      ...tenableFields,
      {
        label: 'Scan Template',
        valueName: 'scanTemplate',
        required: true,
        type: INPUT_TYPES.SELECT,
        defaultValue: 'Basic Network Scan',
        disabled: isDisabled => isDisabled,
        isGetOptionsFromContext: true,
      },
      {
        label: 'Folder Name',
        valueName: 'folderName',
        required: false,
        params: {
          placeholder: 'UVM Scans',
        },
      },
    ],
    fields: [],
  },
  TENABLE_SC: {
    defaultFields: {
      projectionName: 'uber_assets',
      targetMatchingType: TARGET_MATCHING_TYPE_ENUM.CONDITION,
    },
    uiComponentsConfig: {
      disableProjectionSelect: true,
    },
    onPremSection: { shouldShow: true },
    settingsFields: [
      ...tenableFields,
      {
        label: 'Scan Policy',
        valueName: 'scanPolicy',
        required: true,
        type: INPUT_TYPES.SELECT,
        defaultValue: 'Basic Network Scan Policy',
        disabled: isDisabled => isDisabled,
        isGetOptionsFromContext: true,
      },
      {
        label: 'Repository',
        valueName: 'repository',
        required: true,
        type: INPUT_TYPES.SELECT,
        disabled: isDisabled => isDisabled,
        isGetOptionsFromContext: true,
      },
    ],
    fields: [
      {
        label: 'Proxy DNS',
        valueName: 'proxyDNS',
        required: config => config.proxyEnabled,
        shouldShow: ({ proxyEnabled }) => proxyEnabled,
      },
      { label: 'Skip SSL', valueName: 'skipSSL', required: false, type: INPUT_TYPES.BOOL, defaultValue: false },
    ],
  },
};
